import { AppAudioPlayer } from "../audio/AppAudioPlayer";
import { AudioPlayer } from "../audio/AudioPlayer";
import GameScene from "../scenes/GameScene";

/**
 * Overrides Phaser focus handling (disables it).
 * Manually handles loss and regain of focus in app.
 * 
 * On Loss of Focus this will:
 * - mute audio of audio scene (audio player will still handle play/pause/resume of music/sounds correctly), you just wont hear said sound/music.
 * - pause game scene
 * 
 * On Regain of Focus: 
 * - unmute audio of audio scene
 * - unpause game
 * 
 * Aggregates following events:
 * - Phaser.Core.Events.BLUR
 * - Phaser.Core.Events.FOCUS
 * - Phaser.Core.Events.HIDDEN
 * - Phaser.Core.Events.VISIBLE
 * into following events:
 * - FOCUS_LOST
 * - FOCUS_RECEIVED
 * (with only one event dispatched - eg if BLUR and HIDDEN both triggered, 
 * FOCUS_LOST is dispatched only once)
 * 
 * LINKS
 * https://blog.ourcade.co/posts/2020/phaser-3-web-audio-best-practices-games/
 */
export enum FocusEvents {
    FOCUS_LOST = "focus_lost",
    FOCUS_RECEIVED = "focus_received",
}

/**
 * Handle focus gain/loss in a browser window
 * - mute/unmute sounds
 * - ..
 */
export class FocusManager {
    public static SCENE_KEY: string = "focusManager";
    public static scene: Phaser.Scene;

    protected static DEBUG: boolean = false;
    protected static eventEmitter: Phaser.Events.EventEmitter;
    protected static audioScene: Phaser.Scene;
    protected static gameScene: Phaser.Scene;
    protected static curFocus: FocusEvents;
    private static pauseGameOnFocusLoss: boolean;


    public static GetEmitter(): Phaser.Events.EventEmitter {
        return this.eventEmitter;
    }

    public static Init(scene: Phaser.Scene, pauseGameOnFocusLoss: boolean): void {
        // use FocusManager implementation to handle focus loss/gain on everything, except iOS Safari
        // on iOS Safari, use default phaser focus gain/loss handling
        // https://github.com/phaserjs/phaser/issues/4790
        // https://newdocs.phaser.io/docs/3.54.0/Phaser.Device.Browser
        if (scene.sys.game.device.os.iOS) {
            // EDIT: unreliable browser detection by Phaser (@see https://github.com/phaserjs/phaser/issues/6739#issue-2135123108)
            // if (scene.sys.game.device.browser.safari || scene.sys.game.device.browser.mobileSafari) {
            //     // use default Phaser focus handling
            //     return;
            // }

            // @see chromium iOS detection:
            // https://chromium.googlesource.com/chromium/src/+/master/docs/ios/user_agent.md
            if (scene.sys.game.device.browser.mobileSafari && (navigator.userAgent.indexOf('CriOS') == -1)) {
                return;
            }
        }

        this.scene = scene.scene.add(this.SCENE_KEY, focusManagerScene, true); // autostart?
        this.pauseGameOnFocusLoss = pauseGameOnFocusLoss;
        // console.log(pauseGameOnFocusLoss);
        
        this.eventEmitter = new Phaser.Events.EventEmitter();

        this.audioScene = this.scene.scene.get(AudioPlayer.SCENE_KEY);
        // stop Phaser to auto pause audio on BLUR and auto resume audio on FOCUS
        this.audioScene.sound.pauseOnBlur = false;
        this.gameScene = this.scene.scene.get(GameScene.instance.scene.key);

        // this.scene.game.events.on(Phaser.Core.Events.BLUR, this.OnFocusLost, this);
        // document.addEventListener('visibilitychange', this.OnVisibilityChange);

        this.scene.game.events.on(Phaser.Core.Events.BLUR, this.OnBlur, this);
        this.scene.game.events.on(Phaser.Core.Events.FOCUS, this.OnFocus, this);
        this.scene.game.events.on(Phaser.Core.Events.HIDDEN, this.OnHidden, this);
        this.scene.game.events.on(Phaser.Core.Events.VISIBLE, this.OnVisible, this);
        this.scene.game.events.on(Phaser.Core.Events.PAUSE, this.OnPause, this);
        this.scene.game.events.on(Phaser.Core.Events.RESUME, this.OnResume, this);
    }
    /**
        The Game Blur Event.
        This event is dispatched by the Game Visibility Handler when the window in which the Game instance is embedded enters a blurred state. The blur event is raised when the window loses focus. This can happen if a user swaps tab, or if they simply remove focus from the browser to another app.
        NOTE: in WIN desktop Firefox, doesnt trigger when switching Tabs (might be bug: )
        NOTE: SPECIAL: OnBlur and onFocus is the only thing that does trigger when right clicking browser shortcut in taskbar and opening another browser window from there, where onVisible and onHidden DOES NOT trigger (so we need to use both)
     */
    private static OnBlur(): void {
        if (this.DEBUG) console.log("OnBlur", this.scene.time.now | 0);
        this.OnFocusLost();
    }
    /**
        The Game Focus Event.
        This event is dispatched by the Game Visibility Handler when the window in which the Game instance is embedded enters a focused state. The focus event is raised when the window re-gains focus, having previously lost it.
        NOTE: SPECIAL: OnBlur and onFocus is the only thing that does trigger when right clicking browser shortcut in taskbar and opening another browser window from there, where onVisible and onHidden DOES NOT trigger (so we need to use both)
     */
    private static OnFocus(): void {
        if (this.DEBUG) console.log("OnFocus", this.scene.time.now | 0);
        this.OnFocusReceived();
    }
    /**
        The Game Hidden Event.

        This event is dispatched by the Game Visibility Handler when the document in which the Game instance is embedded enters a hidden state. Only browsers that support the Visibility API will cause this event to be emitted.

        In most modern browsers, when the document enters a hidden state, the Request Animation Frame and setTimeout, which control the main game loop, will automatically pause. There is no way to stop this from happening. It is something your game should account for in its own code, should the pause be an issue (i.e. for multiplayer games)
     */
    private static OnHidden(): void {
        if (this.DEBUG) console.log("OnHidden", this.scene.time.now | 0);
        this.OnFocusLost();
    }
    /**
        The Game Visible Event.

        This event is dispatched by the Game Visibility Handler when the document in which the Game instance is embedded enters a visible state, previously having been hidden.

        Only browsers that support the Visibility API will cause this event to be emitted
     */
    private static OnVisible(): void {
        if (this.DEBUG) console.log("OnVisible", this.scene.time.now | 0);
        this.OnFocusReceived();
    }
    /**
        The Game Pause Event.

        This event is dispatched when the Game loop enters a paused state, usually as a result of the Visibility Handler.
    */
    private static OnPause(): void {
        if (this.DEBUG) console.log("OnPause", this.scene.time.now | 0);
        this.OnFocusLost();
    }
    /**
        The Game Resume Event.

        This event is dispatched when the game loop leaves a paused state and resumes running.
    */
    private static OnResume(): void {
        if (this.DEBUG) console.log("OnResume", this.scene.time.now | 0);
        this.OnFocusReceived();
    }

    /**
     * Focus was lost on window with this app.
     * This can happen if a user swaps tab, or if they simply remove focus from the browser to another app.
     */
    protected static OnFocusLost(): void {
        // console.log("OnFocusLost",this.curFocus);
        if (this.curFocus != FocusEvents.FOCUS_LOST) {
            // this.audioScene.sound.mute = true;
            // AppAudioPlayer.SetMusicEnabled(false, false);
            AppAudioPlayer.SetHasFocus(false);
            if (this.pauseGameOnFocusLoss) {
                // this.scene.scene.pause();
                this.gameScene.scene.pause();
            }
            this.eventEmitter.emit(FocusEvents.FOCUS_LOST);

            this.curFocus = FocusEvents.FOCUS_LOST;
        }
    }

    protected static OnFocusReceived(): void {
        // console.log("OnFocusReceived",this.curFocus);
        if (this.curFocus != FocusEvents.FOCUS_RECEIVED) {
            // this.audioScene.sound.mute = false;
            // AppAudioPlayer.SetMusicEnabled(true, false);
            AppAudioPlayer.SetHasFocus(true);
            if (this.pauseGameOnFocusLoss) {
                this.gameScene.scene.resume(this.gameScene.scene.key);
            }
            this.eventEmitter.emit(FocusEvents.FOCUS_RECEIVED);

            this.curFocus = FocusEvents.FOCUS_RECEIVED;
        }
    }
}

export class focusManagerScene extends Phaser.Scene { }